import { useEffect, useState } from 'react';
import { Box, FormField, Loader, Text, Button } from 'wix-style-react';
import { Copy } from 'src/modules/events/components/copy';
import { APIKeysService, CreateAPIKeyResponse } from 'src/redesign/features/Developers/services/APIKeysService';
import { useAppDispatch } from 'src/hooks';
import { showNotification } from 'src/redesign/features/Toast/store/actions';

export const ZapierModal = () => {
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [zapierKeyExists, setZapierKeyExists] = useState(false);
  const dispatch = useAppDispatch();

  const fetchAPIKeys = async () => {
    setLoading(true);
    try {
      const response = await APIKeysService.getAPIKeys();

      // Check if there's a key with name "zapier" (case insensitive)
      const zapierKey = response.api_keys.find(
        (key) => key.name.toLowerCase() === 'zapier' && key.is_active
      );

      if (zapierKey) {
        // If zapier key exists, show the preview (actual key won't be available)
        setZapierKeyExists(true);
        setSecretKey(zapierKey.key_preview);
      } else {
        setZapierKeyExists(false);
        setSecretKey(null);
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      dispatch(
        showNotification({
          detail: 'Failed to load API keys',
          severity: 'error',
          life: 3000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchAPIKeys();
  }, []);

  const handleGenerateKey = async () => {
    setGenerating(true);
    try {
      const response: CreateAPIKeyResponse = await APIKeysService.createAPIKey({ name: 'Zapier' });

      // Set the full key (only available once)
      setSecretKey(response.key);
      setZapierKeyExists(true);

      dispatch(
        showNotification({
          detail: 'Zapier API key generated successfully',
          severity: 'success',
          life: 3000,
        }),
      );
    } catch (error) {
      console.error('Failed to generate Zapier key:', error);
      dispatch(
        showNotification({
          detail: 'Failed to generate Zapier API key',
          severity: 'error',
          life: 3000,
        }),
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box direction="vertical" gap={4}>
      <Text>
        To connect Ticket Spot with your Zapier account, simply grab your Zapier API key from the
        text box below. When setting up a{' '}
        <a
          href="https://zapier.com/apps/ticket-spot/integrations"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ticket Spot Zap
        </a>{' '}
        for the first time, just paste the secret key into the designated field. It&apos;s that
        easy!
      </Text>

      {loading ? (
        <Box align="center" paddingTop={3} paddingBottom={3}>
          <Loader size="medium" />
        </Box>
      ) : zapierKeyExists && secretKey ? (
        <>
          <FormField label="Zapier API Key:">
            <Copy text={secretKey} isTextArea={false} />
          </FormField>
          {secretKey.includes('***') && (
            <Text size="small" secondary>
              Note: This is a preview of your existing key. The full key was only shown once when created.
            </Text>
          )}
        </>
      ) : (
        <Box direction="vertical" gap={2}>
          <Text>No Zapier API key found. Generate one to get started.</Text>
          <Button onClick={handleGenerateKey} disabled={generating}>
            {generating ? 'Generating...' : 'Generate Zapier API Key'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
